<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useThrottleFn } from '@vueuse/core'
import { getBoundingClientRectCompatible, isBeyondViewPortX, isBeyondViewPortY } from './js/index'
import { scrollTo, scrollToX } from '@/utils/scrollTo'
import Icon from '@/components/Icon/index.vue'
import { ListItem } from './js/type'
import { useUserStore } from '@/store/modules/user'

export default {
  name: 'GuideRemind'
}
</script>

<script setup lang="ts">
interface GuideConfigItem {
  lists: ListItem[]
  skipCallBackFun?: () => void
  pageCompleteCallBackFun?: () => void
  isLastPage: boolean
  destroyAppFun?: () => void
}

const listItemKeys = [
  'selector',
  'overlaySelector',
  'hideTargetFlag',
  'remindHtml',
  'remindGap',
  'offsetX',
  'positionX',
  'positionY',
  'showHighlightWrapper',
  'isClipHighlight',
  'hideMask',
  'targetPadding',
  'spotPointLists',
  'beforeCurStepFun',
  'scrollToView',
  'blankSelector'
] as Array<keyof ListItem>

const props = withDefaults(defineProps<GuideConfigItem>(), {
  skipCallBackFun: () => undefined,
  pageCompleteCallBackFun: () => undefined,
  destroyAppFun: () => undefined
})

let curIndex = ref(0)
// 当前步骤的配置
let curConfig: ListItem = reactive({
  ...props.lists[curIndex.value]
})
// 当前指引目标元素的z-index
let curGuideTargetZIndex = ''
// 在弹窗情况下的垂直滚动距离
let scrollDistanceY = 0

const stepsNum = computed(() => {
  return props.lists.length
})
const isCurTop = computed(() => {
  return curConfig.positionY === 'top'
})
const curTargetPadding = computed(() => {
  return curConfig.showHighlightWrapper && curConfig.targetPadding ? curConfig.targetPadding : 5
})
const curRemindGap = computed(() => {
  return Number(curConfig.remindGap) || 30
})
const curSpotPointLists = computed(() => {
  const getSpotPointLists = curConfig.spotPointLists
  return getSpotPointLists && getSpotPointLists.length > 0 ? getSpotPointLists : []
})

watch(
  () => curIndex.value,
  () => {
    const getCurList = props.lists[curIndex.value]
    if (getCurList) {
      listItemKeys.forEach((key) => {
        ;(curConfig[key] as any) = getCurList[key]
      })
      curGuideTargetZIndex = ''
      scrollDistanceY = 0
    }
  },
  {
    immediate: true
  }
)

onMounted(() => {
  init()
  window.addEventListener('resize', listenerResize)
})

const init = () => {
  if (
    props.lists.length === 0 ||
    props.skipCallBackFun === undefined ||
    props.pageCompleteCallBackFun === undefined ||
    props.isLastPage === undefined
  ) {
    console.log('error: 缺少必传参数')
    doDestroy()
    return
  }
  curIndex.value = 0
  handleLayoutGuide(true)
}

const handleLayoutGuide = async (initFlag = false) => {
  await nextTick()
  // 渲染指引前若有钩子函数，则执行
  if (curConfig.beforeCurStepFun) {
    await curConfig.beforeCurStepFun()
  }
  await nextTick()
  /** 无锚点步骤：不依赖 selector，提示框置于视口中央 */
  if (curConfig.blankSelector) {
    setTimeout(
      () => {
        scrollDistanceY = 0
        const coverDom = document.querySelector('.guide-cover-panel') as HTMLElement
        coverDom.style.width = document.body.scrollWidth + 'px'
        coverDom.style.height = document.body.scrollHeight + 'px'

        const highlightDom = document.querySelector('.guide-highlight-wrapper') as HTMLElement
        highlightDom.style.width = '0'
        highlightDom.style.height = '0'
        highlightDom.style.left = '-9999px'
        highlightDom.style.top = '0'
        highlightDom.style.visibility = 'hidden'

        const guideMaskDom = document.querySelector('.guide-mask') as HTMLElement
        if (guideMaskDom) {
          guideMaskDom.style.clipPath = 'none'
        }

        const remindWrapper = document.querySelector('.guide-remind-wrapper') as HTMLElement
        const sx = window.scrollX || document.documentElement.scrollLeft
        const sy = window.scrollY || document.documentElement.scrollTop
        const vw = window.innerWidth
        const vh = window.innerHeight
        const left = sx + (vw - remindWrapper.offsetWidth) / 2 + (curConfig.offsetX || 0)
        const top = sy + (vh - remindWrapper.offsetHeight) / 2
        remindWrapper.style.left = `${Math.max(5, left)}px`
        remindWrapper.style.top = `${Math.max(5, top)}px`
      },
      initFlag ? 300 : 10
    )
    return
  }

  const guideTarget = document.querySelector(curConfig.selector) as HTMLElement | null
  if (guideTarget) {
    setTimeout(
      () => {
        // 将目标元素层次提高，盖在遮罩层上
        if (!curConfig.hideTargetFlag) {
          if (curGuideTargetZIndex === '') {
            curGuideTargetZIndex =
              guideTarget.style.zIndex ||
              window.getComputedStyle(guideTarget, null).zIndex ||
              'auto'
          }
          const targetPosition =
            guideTarget.style.position || window.getComputedStyle(guideTarget, null).position
          if (targetPosition === 'static') {
            guideTarget.style.position = 'relative'
          }
          guideTarget.style.zIndex = '9999997'
        }

        // 若存在弹窗
        if (curConfig.overlaySelector) {
          const overlayDoms = document.querySelectorAll(curConfig.overlaySelector)
          let overlayDom: HTMLElement | null = null
          for (let index = 0, len = overlayDoms.length; index < len; index++) {
            const curDom = overlayDoms[index] as HTMLElement
            if (curDom.contains(guideTarget)) {
              overlayDom = curDom
              break
            }
          }
          if (overlayDom) {
            // 遮罩层处理
            curConfig.hideMask = true
            overlayDom.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'
            overlayDom.style.pointerEvents = 'none'

            // 提示框位置在下时，处理不足位置的情况
            if (!isCurTop.value) {
              // 目标元素离底部的距离（注意：bottom为元素底部到视窗顶部的距离；right为元素右边到视窗左边的距离）
              const getTargetBottom =
                overlayDom.offsetHeight - getBoundingClientRectCompatible(guideTarget).bottom
              // 提示框高度
              const remindDomH = (document.querySelector('.guide-remind-wrapper') as HTMLElement)
                .offsetHeight
              const needH = remindDomH + curRemindGap.value + 30
              // 底部距离 不足以完整展示提示框 时
              if (getTargetBottom < needH) {
                // 底部保留相应距离
                guideTarget.style.marginBottom = needH + 'px'
                // 若出现滚动条，则滚动至底部
                const scrollH = overlayDom.scrollHeight
                const offsetH = overlayDom.offsetHeight
                if (scrollH > offsetH) {
                  overlayDom.scrollTop = scrollH
                  scrollDistanceY = scrollH - offsetH
                }
              }
            }
          }
        }

        // 获取目标元素相关定位数据
        const gRect = getBoundingClientRectCompatible(guideTarget)
        let guideTargetX = Math.max(guideTarget.offsetLeft, gRect.left)
        let guideTargetY = Math.max(guideTarget.offsetTop, gRect.top)
        if (curConfig.scrollToView) {
          const rect = guideTarget.getBoundingClientRect()
          guideTargetX = rect.left
          guideTargetY = rect.top
        }
        const guideTargetW = guideTarget.offsetWidth
        const guideTargetH = guideTarget.offsetHeight
        const getCurTargetPadding = curTargetPadding.value

        // 遮挡层覆盖全屏
        const coverDom = document.querySelector('.guide-cover-panel') as HTMLElement
        coverDom.style.width = document.body.scrollWidth + 'px'
        coverDom.style.height = document.body.scrollHeight + 'px'

        // 定位高亮聚焦白框到目标元素的外围
        const highlightDom = document.querySelector('.guide-highlight-wrapper') as HTMLElement
        highlightDom.style.width = guideTargetW + getCurTargetPadding * 2 + 'px'
        highlightDom.style.height = guideTargetH + getCurTargetPadding * 2 + 'px'
        highlightDom.style.left = guideTargetX - getCurTargetPadding + 'px'
        highlightDom.style.top = guideTargetY - getCurTargetPadding + 'px'

        // 根据 高亮聚焦白框 裁剪出 目标指引元素 展示区域
        if (curConfig.isClipHighlight) {
          const guideMaskDom = document.querySelector('.guide-mask') as HTMLElement
          if (guideMaskDom) {
            const leftHighlight = +(highlightDom.style.left || '').replace('px', '')
            const xStart = leftHighlight < 0 ? 0 : leftHighlight
            const widthHighlight = +(highlightDom.style.width || '').replace('px', '')
            const heightHighlight = +(highlightDom.style?.height || '').replace('px', '')
            const topHighlight = +(highlightDom.style?.top || '').replace('px', '')
            guideMaskDom.style.clipPath = `polygon(
              0 0, 100vw 0, 100vw 100vh, 0 100vh,
              0 ${topHighlight + heightHighlight}px, ${widthHighlight}px ${
              topHighlight + heightHighlight
            }px,
              ${widthHighlight}px ${topHighlight}px, ${xStart}px ${topHighlight}px
            )`
          }
        } else {
          const guideMaskDom = document.querySelector('.guide-mask') as HTMLElement
          if (guideMaskDom) {
            guideMaskDom.style.clipPath = 'none'
          }
        }

        // 根据 高亮聚焦白框 定位提示框的位置
        const remindWrapper = document.querySelector('.guide-remind-wrapper') as HTMLElement
        const remindWrapperWidth = remindWrapper.offsetWidth
        const highlightDomLeft = Number(highlightDom.style.left.replace('px', ''))
        // 横坐标
        const getX = curConfig.positionX || 'center'
        let getLeft = 0
        switch (getX) {
          case 'left': {
            getLeft = highlightDomLeft
            break
          }
          case 'center': {
            getLeft =
              highlightDomLeft +
              Math.floor(highlightDom.offsetWidth / 2) -
              Math.floor(remindWrapperWidth / 2)
            break
          }
          case 'right': {
            getLeft = highlightDomLeft + highlightDom.offsetWidth - remindWrapperWidth
            break
          }
        }
        // 加上偏移距离
        getLeft = getLeft + (curConfig.offsetX || 0)
        // 超出可视区域调整
        getLeft = getLeft < 0 ? 5 : getLeft
        // 获取适配后的距离
        remindWrapper.style.left = getLeft + 'px'
        // 纵坐标
        const highlightDomTop = Number(highlightDom.style.top.replace('px', ''))
        if (isCurTop.value) {
          const getTop = highlightDomTop - curRemindGap.value - remindWrapper.offsetHeight
          // 若超出上方视图区域
          if (getTop < 5) {
            curConfig.positionY = 'bottom'
            handleLayoutGuide()
            return
          }
          remindWrapper.style.top = getTop + 'px'
        } else {
          const highlightHeight = Number(highlightDom.style.height.replace('px', ''))
          remindWrapper.style.top =
            highlightDomTop + highlightHeight + curRemindGap.value - scrollDistanceY + 'px'
        }

        // 逐个渲染指示圆点 和 连接线
        renderPointAndLine({
          guideTargetX,
          guideTargetY,
          guideTargetW,
          guideTargetH
        })

        // 定位后滚动到相应位置
        adaptViewPortX()
        adaptViewPortY()
      },
      initFlag ? 300 : 10
    )
  } else {
    console.log('目标元素获取失败！请检查selector值是否正确，以及注意在dom渲染完成后调用方法。')
    doDestroy()
  }
}
const renderPointAndLine = ({
  guideTargetX,
  guideTargetY,
  guideTargetW,
  guideTargetH
}: {
  [key: string]: number
}) => {
  const spotPointDoms = document.querySelectorAll('.guide-spot-point')
  const linkPointLineDoms = document.querySelectorAll('.guide-link-point-line')
  curSpotPointLists.value.forEach((configItem, index) => {
    // 设置指示圆点位置
    const { type, xValue, yValue } = configItem
    const getXValue = typeof xValue === 'function' ? xValue() : xValue
    const getYValue = typeof yValue === 'function' ? yValue() : yValue

    const spotPoint = spotPointDoms[index] as HTMLElement
    const halfW = 5
    const halfH = 5
    if (type === 'scale') {
      spotPoint.style.left = guideTargetX + Math.floor(guideTargetW * getXValue) - halfW + 'px'
      spotPoint.style.top =
        guideTargetY + Math.floor(guideTargetH * getYValue) - halfH - scrollDistanceY + 'px'
    }
    if (type === 'distance') {
      spotPoint.style.left = guideTargetX + getXValue - halfW + 'px'
      spotPoint.style.top = guideTargetY + getYValue - halfH - scrollDistanceY + 'px'
    }
    // 设置线条位置和长度
    const linkPointLine = linkPointLineDoms[index] as HTMLElement
    linkPointLine.style.left = Number(spotPoint.style.left.replace('px', '')) + halfW + 'px'
    if (isCurTop.value) {
      const getTop = guideTargetY - curRemindGap.value - 5
      linkPointLine.style.top = getTop + 'px'
      linkPointLine.style.height = Number(spotPoint.style.top.replace('px', '')) - getTop + 'px'
    } else {
      const getTop = Number(spotPoint.style.top.replace('px', '')) + spotPoint.offsetHeight
      linkPointLine.style.top = getTop + 'px'
      const remindWrapper = document.querySelector('.guide-remind-wrapper') as HTMLElement
      linkPointLine.style.height = Number(remindWrapper.style.top.replace('px', '')) - getTop + 'px'
    }
  })
}
// 适配水平位置，确保滚动到提示区域
const adaptViewPortX = () => {
  const remindWrapper = document.querySelector('.guide-remind-wrapper') as HTMLElement
  const getCheck = isBeyondViewPortX(remindWrapper)
  if (getCheck === 'left') {
    scrollToX(remindWrapper.offsetLeft)
  }
  if (getCheck === 'right') {
    scrollToX(remindWrapper.offsetLeft + remindWrapper.offsetWidth)
  }
}
// 适配垂直位置，确保滚动到提示区域
const adaptViewPortY = () => {
  const remindWrapper = document.querySelector('.guide-remind-wrapper') as HTMLElement
  const highlightDom = document.querySelector('.guide-highlight-wrapper') as HTMLElement
  const getCheck = isBeyondViewPortY(remindWrapper)
  if (getCheck) {
    if (isCurTop.value) {
      if (getCheck === 'top') {
        scrollTo(remindWrapper.offsetTop - 30)
      }
      if (getCheck === 'down') {
        scrollTo(highlightDom.offsetTop + highlightDom.offsetHeight + 30)
      }
    } else {
      if (getCheck === 'top') {
        scrollTo(highlightDom.offsetTop - 30)
      }
      if (getCheck === 'down') {
        scrollTo(remindWrapper.offsetTop + remindWrapper.offsetHeight + 30)
      }
    }
  }
}
const listenerResize = useThrottleFn(() => {
  handleLayoutGuide(true)
}, 500)
// 将当前步骤的目标元素层次恢复
const recoverGuideTargetStyle = () => {
  if (curConfig.blankSelector) {
    const highlightDom = document.querySelector('.guide-highlight-wrapper') as HTMLElement | null
    if (highlightDom) {
      highlightDom.style.visibility = ''
    }
    return
  }
  const guideTarget = document.querySelector(curConfig.selector) as HTMLElement
  if (guideTarget) {
    guideTarget.style.zIndex = curGuideTargetZIndex || 'auto'
  }
}
const doSkip = () => {
  useUserStore().setShowGuide(false)
  recoverGuideTargetStyle()
  doDestroy()
  props.skipCallBackFun()
}
const doNext = () => {
  recoverGuideTargetStyle()
  // 进行下一步
  curIndex.value++
  // 若当前页面完成所有指引
  if (curIndex.value >= stepsNum.value) {
    // 最后一页时，相当于点击跳过
    if (props.isLastPage) {
      doSkip()
    } else {
      props.pageCompleteCallBackFun()
    }
    return
  }
  // 开始下一步的渲染
  handleLayoutGuide()
}
const doDestroy = async () => {
  await nextTick()
  window.removeEventListener('resize', listenerResize)
  props.destroyAppFun()
}
</script>

<template>
  <div v-if="curConfig">
    <!-- 遮罩层(z-index: 999999) -->
    <div v-if="!curConfig.hideMask" class="guide-mask" />
    <!-- 高亮聚焦白框(z-index: 9999996) -->
    <div
      class="guide-highlight-wrapper"
      :class="{ 'visibility-hidden': !curConfig.showHighlightWrapper }"
    />
    <!-- 目标元素(z-index: 9999997) -->
    <!-- 遮挡层(z-index: 9999998) （防止用户操作目标元素）-->
    <div class="guide-cover-panel" />
    <!-- 提示层(z-index: 9999999) -->
    <div class="guide-remind-wrapper">
      <!-- 提示框 -->
      <div class="guide-remind-dialog">
        <Icon icon="guide-remind-star|svg" class="guide-remind-star" />
        <Icon icon="guide-remind-note|svg" class="guide-remind-note" />
        <img class="guide-remind-bg" src="@/assets/images/guide-remind-bg.png" />
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="remind-text" v-html="curConfig.remindHtml" />
      </div>
      <!-- 操作按钮 -->
      <div class="guide-handle-btn" :class="{ top: isCurTop }">
        <span v-if="!(isLastPage && curIndex === stepsNum - 1)" class="skip-btn" @click="doSkip()">
          跳过
        </span>
        <span class="next-btn" @click="doNext()">
          {{ isLastPage && curIndex === stepsNum - 1 ? '完成' : '下一步' }}
        </span>
      </div>
    </div>
    <template v-for="(item, index) in curSpotPointLists" :key="index">
      <!-- 指示圆点(z-index: 9999999) -->
      <div class="guide-spot-point" />
      <!-- 指示圆点与提示层之间连接线(z-index: 9999999) -->
      <div class="guide-link-point-line" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.guide-mask {
  z-index: 999999;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0.8;
  background-color: rgba(0, 0, 0, 0.6);
}
.guide-remind-wrapper,
.guide-spot-point,
.guide-link-point-line {
  position: absolute;
  z-index: 9999999;
}
.guide-cover-panel {
  position: absolute;
  z-index: 9999998;
  top: 0;
  left: 0;
}
.guide-highlight-wrapper {
  position: absolute;
  z-index: 9999996;
  background-color: #ffffff;
  border-radius: 6px;
  &.visibility-hidden {
    visibility: hidden;
  }
}
.guide-remind-wrapper {
  max-width: 100%;
  min-width: 170px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.guide-remind-wrapper .guide-remind-dialog {
  order: 1;
  position: relative;
  padding: 15px 62px 15px 20px;
  .guide-remind-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -2;
  }
  .guide-remind-star {
    position: absolute;
    top: -4px;
    left: -6px;
    width: 14.25px !important;
    height: 26px !important;
    z-index: -1;
  }
  .guide-remind-note {
    position: absolute;
    bottom: -6px;
    right: -13px;
    width: 73.56px !important;
    height: 64.14px !important;
    z-index: -1;
  }
  .remind-text {
    min-width: 120px;
    line-height: 1.9;
    font-size: 16px;
    color: #ffffff;
    white-space: nowrap;
  }
}
.guide-remind-wrapper .guide-handle-btn {
  order: 2;
  margin-top: 26px;
  margin-bottom: 13px;
  position: relative;
  left: -1%;
  text-align: center;
  &.top {
    order: 0;
    margin-bottom: 26px;
  }
  span {
    padding: 6px 20px;
    display: inline-block;
    vertical-align: middle;
    font-size: 14px;
    text-align: center;
    border-radius: 16px;
    cursor: pointer;
  }
  .skip-btn {
    margin-right: 16px;
    color: #ffffff;
    border: 1px solid #ffffff;
    background: transparent;
  }
  .next-btn {
    color: #555555;
    background: #ffffff;
  }
}
.guide-spot-point {
  width: 11.4px;
  height: 11.4px;
  border-radius: 50%;
  background: #1761f1;
  animation: breathe 3s infinite;
}
.guide-link-point-line {
  width: 1px;
  background: #1761f1;
}

@keyframes breathe {
  0% {
    box-shadow: 0 0 0 0 rgba(39, 132, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(39, 132, 255, 0.2);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(39, 132, 255, 0.2);
  }
}
</style>
